/**
 * Oylik maqsad halqasi (Apple Activity kabi): shu oy tasdiqlangan ishga olishlar maqsadga nisbatan (doira bo'yicha:
 * recruiter -- o'zi, menejer -- filial, owner -- kompaniya). Maqsad -- sozlamadagi oylik son x recruiterlar.
 */
import { useTranslation } from "react-i18next";
import { Target } from "lucide-react";
import { cn, formatMoney } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { useWidgetData } from "../data";
import { Ring, WidgetEmpty } from "../ui";
import { goalStats, type GoalData } from "./math";

type Props = { item: WidgetItem<Record<string, never>>; me: Me };
const ENTITIES = ["driver"];

export function GoalView({ item, me }: Props) {
  const { t } = useTranslation("widgets");
  const q = useWidgetData<GoalData>("goal", {}, ENTITIES);
  const d = q.data;
  if (!d) return q.isError ? <WidgetEmpty text={t("failed")} /> : null;
  const s = goalStats(d);
  const tone = s.reached ? "text-success" : d.goal > 0 ? "text-ink" : "text-faint";
  const ring = (
    <Ring value={s.pct} size={88} stroke={8} className={tone}>
      <div className="text-[20px] leading-none font-semibold tracking-tight tabular-nums">
        {d.hired}
        {d.goal > 0 && <span className="text-[13px] text-muted">/{d.goal}</span>}
      </div>
      <div className="mt-0.5 text-[10px] text-muted">{t("goal.hires")}</div>
    </Ring>
  );
  if (item.size === "s") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1.5 px-3 py-2">
        {ring}
        <div className={cn("text-[11px] tabular-nums", d.goal === 0 ? "text-muted" : s.diff > 0 ? "text-success" : s.diff < 0 ? "text-danger" : "text-muted")}>
          {d.goal > 0 ? t(s.diff > 0 ? "goal.ahead" : s.diff < 0 ? "goal.behind" : "goal.onPace", { count: Math.abs(s.diff) }) : t("goal.noGoal")}
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full items-center gap-3 px-3 py-2.5">
      {ring}
      <div className="min-w-0 flex-1 space-y-0.5 border-l border-line pl-3 text-[12px]">
        <div className="font-semibold">{t("goal.monthTitle")}</div>
        {d.goal > 0 ? (
          <>
            <div className="text-muted">{t("goal.remaining", { count: s.remaining })}</div>
            <div className={cn("tabular-nums", s.diff > 0 ? "text-success" : s.diff < 0 ? "text-danger" : "text-muted")}>
              {t(s.diff > 0 ? "goal.ahead" : s.diff < 0 ? "goal.behind" : "goal.onPace", { count: Math.abs(s.diff) })} · {t("goal.day", { day: d.day, days: d.days })}
            </div>
          </>
        ) : (
          <div className="text-muted">{t(me.role === "owner" ? "goal.noGoalOwner" : "goal.noGoal")}</div>
        )}
        {Number(d.bonus_per_hire) > 0 && <div className="text-[11px] text-muted">{t("goal.bonus", { amount: formatMoney(Number(d.bonus_per_hire), d.bonus_currency) })}</div>}
      </div>
    </div>
  );
}

export function GoalGlance() {
  const { t } = useTranslation("widgets");
  const q = useWidgetData<GoalData>("goal", {}, ENTITIES);
  const d = q.data;
  return (
    <span className="inline-flex items-center gap-1.5" title={t("goal.title")}>
      <Target size={13} strokeWidth={1.75} className="text-muted" aria-hidden />
      <span className="text-[12px] font-semibold tabular-nums">{d ? (d.goal > 0 ? `${d.hired}/${d.goal}` : d.hired) : "–"}</span>
    </span>
  );
}
